// /week: the team's week at a glance, read-only by default. Supervisors switch
// on edit mode; Approve needs a reason and the server re-checks the hard
// limits; locked half-days stay locked. Shows the plan's state, what it makes
// per day, what breaks, and the planner's proposal side by side.
import { useIonToast } from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Cell, CheckResponse, Day, Forecast, Half, TaskRow, VersionSummary, Violation, WeekView } from "../lib/types";
import { BlockCell, NowLine, SlotHeader, StatusBanner, TaskChip } from "../design";
import { announce, can, useApi, useMe } from "../api/live";
import { ApiError, post } from "../api/client";
import { paths } from "../api/paths";
import { addDays, HALVES, shortDay, slotBefore, weekday, weekLabel } from "../lib/days";
import { sessionFor } from "../lib/blockState";
import { useNow } from "../lib/now";
import { useTasks } from "../lib/useRegister";
import { violationCounts, violationSummary } from "../lib/copy";
import { Page } from "../shell/Page";
import { ErrorNote, Loading, useReason } from "../shell/ui";
import { list, safeWeek } from "../lib/safe";

const key = (c: Pick<Cell, "person" | "day" | "half">) => `${c.person}|${c.day}|${c.half}`;
const sameCell = (a: Cell | undefined, b: Cell | undefined) =>
  !!a && !!b && a.kind === b.kind && (a.task ?? null) === (b.task ?? null) && !!a.learner === !!b.learner && (a.qty ?? null) === (b.qty ?? null) && (a.expert ?? null) === (b.expert ?? null);

/** "Emma Tue AM: Float → A3" for every cell that differs. */
export function diffCells(from: Cell[], to: Cell[]): string[] {
  const was = new Map(from.map((c) => [key(c), c]));
  const label = (c?: Cell) => (!c ? "nothing" : c.kind === "task" ? c.task ?? "?" : c.kind === "float" ? "Float" : c.kind === "off" ? "not in" : "other work");
  return to.filter((c) => !sameCell(c, was.get(key(c)))).map((c) => `${c.person} ${weekday(c.day)} ${c.half}: ${label(was.get(key(c)))} → ${label(c)}`);
}

export function forecastSentence(f: Forecast | null | undefined, days: Day[], lead = "This week makes"): string {
  if (!f) return "No forecast yet";
  const per = days.map((d) => `${weekday(d)} ${f.days[d] ?? 0}`).join(" · ");
  return `${lead} ${f.week} device${f.week === 1 ? "" : "s"}: ${per}`;
}

export function WeekPage() {
  const { me } = useMe();
  const now = useNow();
  const [present] = useIonToast();
  const ask = useReason();
  const [start, setStart] = useState<Day | "current">("current");
  const [version, setVersion] = useState<number | null>(null);
  const week = useApi<WeekView>(paths.week(start, version), ["plan", "session", "stock", "person", "absence"]);
  const { byCode } = useTasks();
  const w = useMemo(() => (week.data ? safeWeek(week.data) : null), [week.data]);

  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState<Cell[] | null>(null);
  const [checked, setChecked] = useState<CheckResponse | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [compare, setCompare] = useState(false);
  const [busy, setBusy] = useState(false);

  const supervisor = can(me, "supervisor");
  // On a phone the grid scrolls sideways: start it at today, not at Monday.
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scroller.current;
    const today = el?.querySelector<HTMLElement>(".slot-day.is-today");
    if (el && today && el.scrollWidth > el.clientWidth) el.scrollLeft = Math.max(0, today.offsetLeft - (el.querySelector<HTMLElement>(".wk__corner")?.offsetWidth ?? 0));
  }, [week.data?.weekStart]);
  const cells = draft ?? w?.cells ?? [];
  const violations = checked?.violations ?? w?.violations ?? [];
  const forecast = checked?.forecast ?? w?.forecast ?? null;
  const approved = w?.versions.find((v) => v.kind === "approved") ?? null;
  const proposal = [...(w?.versions ?? [])].reverse().find((v) => v.kind === "proposal") ?? null;
  const changes = useMemo(() => (draft && w ? diffCells(w.cells, draft) : []), [draft, w]);

  // Leaving edit mode or changing week drops the draft.
  useEffect(() => { setDraft(null); setChecked(null); setSel(null); }, [w?.weekStart, w?.shown?.id]);

  // Every edit is checked against the hard limits on the server (debounced).
  const checkTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!draft || !w) return;
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(() => {
      post<CheckResponse>(paths.check(w.weekStart), { cells: draft }).then(setChecked, () => setChecked(null));
    }, 400);
    return () => clearTimeout(checkTimer.current);
  }, [draft, w]);

  if (!w) return <Page title="Week">{week.error ? <ErrorNote error={week.error} retry={week.reload} /> : <Loading what="Reading the week" />}</Page>;

  const vOf = (c: Cell): "block" | "warn" | null => {
    const hit = violations.filter((v) => v.person === c.person && v.slot?.day === c.day && v.slot?.half === c.half);
    return hit.some((v) => v.kind === "block") ? "block" : hit.length ? "warn" : null;
  };
  const selected = sel ? cells.find((c) => key(c) === sel) ?? null : null;
  const original = sel ? w.cells.find((c) => key(c) === sel) : undefined;
  const today = w.days.includes(now.day) ? now.day : null;

  const update = (c: Cell, patch: Partial<Cell>) => {
    const next = cells.map((x) => (key(x) === key(c) ? { ...x, ...patch } : x));
    setDraft(next);
  };

  const saveDraft = async (reason: string): Promise<VersionSummary | null> => {
    try {
      return await post<VersionSummary>(paths.saveVersion(w.weekStart), { basedOnVersion: w.shown?.version ?? null, cells: draft, reason });
    } catch (e) {
      const err = e as ApiError;
      present({ message: err.locked?.length ? `Locked half-days can't change: ${err.locked.join(", ")}` : err.message, duration: 4000, color: "danger" });
      return null;
    }
  };

  const approve = async (v: number | null, why?: string) => {
    const reason = why ?? (await ask("Approve the plan", "This becomes the plan the floor works from. Why approve it?", "Approve"));
    if (!reason) return;
    setBusy(true);
    try {
      let target = v;
      if (target == null && draft && changes.length) {
        const saved = await saveDraft(reason);
        if (!saved) return;
        target = saved.version;
      }
      target ??= w.shown?.version ?? null;
      if (target == null) return;
      await post<VersionSummary>(paths.approve(w.weekStart), { version: target, reason });
      present({ message: `v${target} approved`, duration: 2500, color: "success" });
      setEdit(false); setDraft(null); setCompare(false); setVersion(null);
      announce(["plan"]);
    } catch (e) {
      const err = e as ApiError;
      if (err.violations?.length) {
        const vs = err.violations;
        setChecked((c) => (c ? { ...c, violations: vs } : { violations: vs, simulation: w.simulation, forecast: w.forecast,
          score: w.shown?.score ?? { devices: 0, swing: 0, shelfDistance: 0, changedCells: 0, preferences: 0, total: 0 } }));
      }
      present({ message: err.code === "blocked" ? `Can't approve: ${err.message}` : err.message, duration: 5000, color: "danger" });
    } finally { setBusy(false); }
  };

  const onSave = async () => {
    const reason = await ask("Save as a draft", "Say why the plan changes. The reason goes into the review.");
    if (!reason) return;
    setBusy(true);
    const v = await saveDraft(reason);
    setBusy(false);
    if (v) { present({ message: `Saved as draft v${v.version}`, duration: 2500 }); setDraft(null); setVersion(v.version); announce(["plan"]); }
  };

  const counts = violationCounts(violations);
  const people = w.people;

  return (
    <Page title="Week">
      <div className="page__head">
        <div className="weekstep">
          <button type="button" className="btn btn--sm btn--ghost" aria-label="Week before" onClick={() => { setVersion(null); setStart(addDays(w.weekStart, -7)); }}>‹</button>
          <h1>{weekLabel(w.weekStart)}</h1>
          <button type="button" className="btn btn--sm btn--ghost" aria-label="Week after" onClick={() => { setVersion(null); setStart(addDays(w.weekStart, 7)); }}>›</button>
        </div>
        <span className="spacer" />
        {w.versions.length ? (
          <label className="row small muted">Version
            <select className="select select--inline" value={w.shown?.version ?? ""} onChange={(e) => { setVersion(Number(e.target.value)); setEdit(false); }}>
              {[...w.versions].reverse().map((v) => <option key={v.id} value={v.version}>v{v.version} {v.kind} · {v.authorName ?? v.authorKind}</option>)}
            </select>
          </label>
        ) : null}
        {supervisor && w.shown ? (
          <button type="button" className={`btn btn--sm${edit ? " btn--primary" : ""}`} aria-pressed={edit} onClick={() => { setEdit(!edit); setDraft(null); setChecked(null); setSel(null); }}>
            {edit ? "Editing" : "Edit"}
          </button>
        ) : null}
      </div>

      <StatusBanner shown={w.shown} proposal={proposal} approved={approved} onCompare={proposal ? () => setCompare(true) : undefined} />

      <p className="forecast" data-testid="forecast">{forecastSentence(forecast, w.days)}{draft ? " (with your changes)" : ""}</p>
      {w.engineError ? <ErrorNote error={new Error(`The planner couldn't check this week: ${w.engineError}`)} /> : null}

      {compare && proposal ? <ProposalCompare week={w} proposal={proposal} approved={approved} canTake={supervisor} busy={busy}
        onTake={(reason) => approve(proposal.version, reason)} onClose={() => setCompare(false)} ask={ask} /> : null}

      <div className={`weekbody${edit ? " is-editing" : ""}`}>
        <div className="wk-scroll" ref={scroller} role="region" aria-label="The week's grid" tabIndex={0}>
          <div className="wk" style={{ gridTemplateRows: `auto auto repeat(${people.length}, auto)` }}>
            <div className="wk__corner" style={{ gridRow: "1 / span 2", gridColumn: 1 }}>
              <span className="oc-label">{people.length} people</span>
            </div>
            <SlotHeader days={w.days} today={today} lockedBefore={w.lockedBefore} />
            {people.map((p, r) => (
              <PersonRow key={p.name} row={r + 3} name={p.name} contract={p.contractText} days={w.days}
                cells={cells} byCode={byCode} week={w} now={{ day: now.day, half: now.half }} edit={edit} sel={sel}
                changed={draft ? new Set(diffKeys(w.cells, draft)) : null} vOf={vOf} onSelect={(c) => setSel(key(c))} />
            ))}
            <div className="wk__now" style={{ gridColumn: "2 / -1", gridRow: `3 / ${people.length + 3}` }}>
              <NowLine days={w.days} now={today ? now : null} />
            </div>
          </div>
        </div>

        <aside className="weekside">
          {edit && selected ? (
            <CellEditor cell={selected} original={original} tasks={Object.values(byCode)} people={people.map((p) => p.name)}
              locked={slotBefore(selected.day, selected.half, w.lockedBefore)} onChange={(patch) => update(selected, patch)} onClose={() => setSel(null)} />
          ) : edit ? (
            <div className="card"><p className="muted small">Tap a half-day to change it. Locked half-days (started, or logged) can't change.</p></div>
          ) : null}
          <ViolationsPanel violations={violations} onPick={(v) => v.person && v.slot && setSel(key({ person: v.person, day: v.slot.day, half: v.slot.half }))} />
          {!supervisor ? <p className="muted small">Read-only. If the plan can't be followed, say so on the bench: "This plan doesn't work".</p> : null}
        </aside>
      </div>

      {edit ? (
        <div className="editbar" role="toolbar" aria-label="Edit">
          <span className={`editbar__said${changes.length ? " is-changed" : ""}`}>
            {changes.length ? `${changes.length} change${changes.length === 1 ? "" : "s"} · ${counts.blocks ? `${counts.blocks} block${counts.blocks === 1 ? "" : "s"}` : "no blocks"}` : "No changes"}
          </span>
          <button type="button" className="btn btn--sm" onClick={() => { setDraft(null); setChecked(null); setEdit(false); }}>Discard</button>
          <button type="button" className="btn btn--sm" disabled={!changes.length || busy} onClick={onSave}>Save draft</button>
          <button type="button" className="btn btn--sm btn--primary" disabled={busy || (!changes.length && w.shown?.kind === "approved") || counts.blocks > 0}
            title={counts.blocks ? "Fix the blocks first: the server refuses a plan that breaks a hard limit" : undefined}
            onClick={() => approve(null)}>Approve…</button>
        </div>
      ) : null}
    </Page>
  );
}

function diffKeys(from: Cell[], to: Cell[]) {
  const was = new Map(from.map((c) => [key(c), c]));
  return to.filter((c) => !sameCell(c, was.get(key(c)))).map(key);
}

function PersonRow(props: {
  row: number; name: string; contract: string | null; days: Day[]; cells: Cell[]; byCode: Record<string, TaskRow>; week: WeekView;
  now: { day: Day; half: Half }; edit: boolean; sel: string | null; changed: Set<string> | null;
  vOf: (c: Cell) => "block" | "warn" | null; onSelect: (c: Cell) => void;
}) {
  const { row, name, contract, days, cells, byCode, week, now, edit, sel, changed, vOf, onSelect } = props;
  return (
    <>
      <div className="wk__name" style={{ gridRow: row, gridColumn: 1 }}>
        <span className="wk__person">{name}</span>
        {contract ? <span className="wk__hours">{contract}</span> : null}
      </div>
      {days.flatMap((d, i) => HALVES.map((h, j) => {
        const c = cells.find((x) => x.person === name && x.day === d && x.half === h) ?? { person: name, day: d, half: h, kind: "off" as const };
        const t = c.task ? byCode[c.task] : undefined;
        return (
          <div key={d + h} className="wk__cell" style={{ gridRow: row, gridColumn: 2 + i * 2 + j }}>
            <BlockCell cell={c} taskName={t?.name} group={t?.group} sessionQty={t?.sessionQty} halfOnly={t?.halfOnly}
              session={sessionFor(week.sessions, c)} now={now} lockedBefore={week.lockedBefore} editable={edit}
              selected={sel === key(c)} changed={changed?.has(key(c))} violation={vOf(c)} onSelect={onSelect} />
          </div>
        );
      }))}
    </>
  );
}

function CellEditor({ cell, original, tasks, people, locked, onChange, onClose }: {
  cell: Cell; original?: Cell; tasks: TaskRow[]; people: string[]; locked: boolean; onChange: (p: Partial<Cell>) => void; onClose: () => void;
}) {
  const value = cell.kind === "task" ? `task:${cell.task}` : cell.kind;
  const t = tasks.find((x) => x.code === cell.task);
  return (
    <div className="card" aria-label="Change this half-day">
      <div className="card__head">
        <h2>{cell.person}, {shortDay(cell.day)} {cell.half}</h2>
        <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>Done</button>
      </div>
      {locked ? <p className="muted small">Locked: this half-day has started. It stays as it is.</p> : (
        <>
          <label className="field"><span className="field__label">Job</span>
            <select className="select" value={value} onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith("task:")) {
                const code = v.slice(5);
                const nt = tasks.find((x) => x.code === code);
                onChange({ kind: "task", task: code, qty: nt ? Math.round(nt.sessionQty * (cell.learner ? 0.5 : 1)) : null });
              } else onChange({ kind: v as Cell["kind"], task: null, qty: null, learner: false, expert: null });
            }}>
              <option value="float">Float</option>
              <option value="off">Not in</option>
              <option value="other">Other work</option>
              {tasks.map((x) => <option key={x.code} value={`task:${x.code}`}>{x.code} {x.name}{x.halfOnly ? ` (${x.halfOnly} only)` : ""}</option>)}
            </select>
          </label>
          {cell.kind === "task" ? (
            <>
              <label className="field"><span className="field__label">Planned units{t ? ` (a full session is ${t.sessionQty})` : ""}</span>
                <input className="input input--num" type="number" min={0} inputMode="numeric" value={cell.qty ?? ""} onChange={(e) => onChange({ qty: e.target.value === "" ? null : Number(e.target.value) })} />
              </label>
              <label className="row small"><input type="checkbox" checked={!!cell.learner} onChange={(e) => onChange({ learner: e.target.checked, qty: t ? Math.round(t.sessionQty * (e.target.checked ? 0.5 : 1)) : cell.qty })} /> Learning this job</label>
              {cell.learner ? (
                <label className="field"><span className="field__label">With expert</span>
                  <select className="select" value={cell.expert ?? ""} onChange={(e) => onChange({ expert: e.target.value || null })}>
                    <option value="">Nobody named</option>
                    {people.filter((p) => p !== cell.person).map((p) => <option key={p}>{p}</option>)}
                  </select>
                </label>
              ) : null}
            </>
          ) : null}
          {original && (original.kind !== cell.kind || original.task !== cell.task) ? (
            <p className="small muted">Was {original.kind === "task" ? <TaskChip task={original.task ?? null} showName={false} /> : original.kind}.
              {" "}<button type="button" className="btn btn--sm btn--ghost" onClick={() => onChange({ ...original })}>Put it back</button></p>
          ) : null}
        </>
      )}
    </div>
  );
}

export function ViolationsPanel({ violations, onPick }: { violations: Violation[]; onPick?: (v: Violation) => void }) {
  const order = { block: 0, warn: 1, advisory: 2 } as const;
  const sorted = [...violations].sort((a, b) => order[a.kind] - order[b.kind]);
  return (
    <section className="card" aria-label="Rules">
      <div className="card__head"><h2>Rules</h2><span className="small muted">{violationSummary(violations)}</span></div>
      {sorted.length ? (
        <ul className="vlist">
          {sorted.map((v, i) => (
            <li key={i} className={`vlist__item vlist__item--${v.kind}`}>
              <button type="button" onClick={() => onPick?.(v)} disabled={!v.slot}>
                <span className={`pill ${v.kind === "block" ? "pill--r" : v.kind === "warn" ? "pill--a" : "pill--muted"}`}>{v.rule}</span>
                <span>{v.message}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : <p className="small muted">Every hard limit holds.</p>}
    </section>
  );
}

function ProposalCompare({ week, proposal, approved, canTake, busy, onTake, onClose, ask }: {
  week: WeekView; proposal: VersionSummary; approved: VersionSummary | null; canTake: boolean; busy: boolean;
  onTake: (reason: string) => void; onClose: () => void; ask: ReturnType<typeof useReason>;
}) {
  const prop = useApi<WeekView>(paths.week(week.weekStart, proposal.version), ["plan"]);
  const base = useApi<WeekView>(approved ? paths.week(week.weekStart, approved.version) : null, ["plan"]);
  const diffs = prop.data && base.data ? diffCells(list(base.data.cells), list(prop.data.cells)) : [];
  const col = (label: string, v: VersionSummary | null, w: WeekView | null) => {
    const c = violationCounts(w?.violations ?? []);
    return (
      <div className="compare__col">
        <span className="oc-label">{label}</span>
        <div className="compare__big">{v?.forecast?.week ?? "–"} <small>devices</small></div>
        <div className="compare__days">{week.days.map((d) => <span key={d}><b>{weekday(d)}</b> {v?.forecast?.days[d] ?? 0}</span>)}</div>
        <div className="small">{w ? `${c.blocks} block${c.blocks === 1 ? "" : "s"}, ${c.warns} warning${c.warns === 1 ? "" : "s"}` : "…"}</div>
        {v?.score ? <div className="small muted">Score {v.score.total.toFixed(0)}</div> : null}
      </div>
    );
  };
  return (
    <section className="card compare" aria-label="Proposal compared with the approved plan">
      <div className="card__head">
        <h2>Proposal v{proposal.version} against {approved ? `approved v${approved.version}` : "no approved plan"}</h2>
        <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>Close</button>
      </div>
      {proposal.reason ? <p className="small muted" style={{ marginBottom: "var(--space-3)" }}>{proposal.authorName}: “{proposal.reason}”</p> : null}
      <div className="compare__cols">
        {col(approved ? `Approved v${approved.version}` : "Approved", approved, base.data)}
        {col(`Proposal v${proposal.version}`, proposal, prop.data)}
      </div>
      <details className="compare__diff">
        <summary>{diffs.length} half-day{diffs.length === 1 ? "" : "s"} change</summary>
        <ul>{diffs.map((d) => <li key={d}>{d}</li>)}</ul>
      </details>
      {canTake ? (
        <div className="row" style={{ marginTop: "var(--space-3)" }}>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={async () => {
            const r = await ask("Take the proposal", "Approving the proposal replaces the plan for the rest of the week. Why?", "Approve");
            if (r) onTake(r);
          }}>Take the proposal…</button>
          <button type="button" className="btn" onClick={onClose}>Leave it</button>
        </div>
      ) : null}
    </section>
  );
}
